import { useState, useEffect } from 'react';

// Get the base API URL (without /api/customer-portal suffix)
function getAffiliateApiBaseUrl(): string {
  // Use environment variable if set
  const envApiUrl = import.meta.env.VITE_API_URL;
  if (envApiUrl) {
    return envApiUrl;
  }

  // Fallback based on hostname
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:7002';
  }
  return 'https://api.aiva.nz';
}

// Affiliate Portal Types
interface AffiliateSession {
  affiliateId: string;
  name: string;
  email: string;
  affiliateCode: string;
  isCustomerReferrer: boolean;
  commissionRate: number;
  customerDiscountRate: number;
  status: string;
  storeName: string;
}

interface AffiliateDashboard {
  affiliate: {
    id: string;
    name: string;
    email: string;
    affiliateCode: string;
    commissionRate: number;
    customerDiscountRate: number;
    status: string;
    isCustomerReferrer: boolean;
    createdAt: string;
  };
  stats: {
    totalOrders: number;
    totalSales: number;
    totalCommissions: number;
    unpaidCommissions: number;
    paidCommissions: number;
    conversionRate: number;
    lifetimeValue: number;
  };
  referralLink: string;
  discountCode: string;
  storeName: string;
  websiteUrl: string;
}

interface AffiliateSale {
  id: string;
  orderId: string;
  orderDate: string;
  orderTotal: number;
  commissionAmount: number;
  commissionRate: number;
  paymentStatus: string;
  customerName: string;
}

interface AffiliatePayout {
  id: string;
  amount: number;
  status: string;
  method: string;
  requestedAt: string;
  processedAt?: string;
  reference?: string;
}

interface PayoutsResponse {
  pendingPayoutAmount: number;
  payouts: AffiliatePayout[];
}

export default function Affiliate() {
  const [affiliateToken, setAffiliateToken] = useState<string | null>(null);
  const apiBaseUrl = getAffiliateApiBaseUrl();
  const [dashboard, setDashboard] = useState<AffiliateDashboard | null>(null);
  const [sales, setSales] = useState<AffiliateSale[]>([]);
  const [payouts, setPayouts] = useState<PayoutsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'sales' | 'payouts'>('overview');
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [payoutMessage, setPayoutMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Get affiliate token from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('affiliate_token') || urlParams.get('token');
    if (token) {
      setAffiliateToken(token);
    } else {
      setError('No affiliate token provided. Please use a valid affiliate link.');
      setLoading(false);
    }
  }, []);

  // Load affiliate data
  useEffect(() => {
    if (!affiliateToken) return;

    async function loadAffiliateData() {
      try {
        setLoading(true);
        setError(null);

        // Fetch dashboard data
        const dashboardResponse = await fetch(`${apiBaseUrl}/api/affiliate-portal/${affiliateToken}/dashboard`);
        if (!dashboardResponse.ok) {
          if (dashboardResponse.status === 401) {
            throw new Error('Invalid or expired affiliate token');
          }
          throw new Error('Failed to load affiliate dashboard');
        }
        const dashboardData = await dashboardResponse.json();
        setDashboard(dashboardData);

        // Fetch sales data
        const salesResponse = await fetch(`${apiBaseUrl}/api/affiliate-portal/${affiliateToken}/sales?limit=50`);
        if (salesResponse.ok) {
          const salesData = await salesResponse.json();
          setSales(salesData.sales || []);
        }

        // Fetch payout data
        const payoutsResponse = await fetch(`${apiBaseUrl}/api/affiliate-portal/${affiliateToken}/payouts`);
        if (payoutsResponse.ok) {
          const payoutsData = await payoutsResponse.json();
          setPayouts(payoutsData);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load affiliate data');
      } finally {
        setLoading(false);
      }
    }

    loadAffiliateData();
  }, [affiliateToken]);

  const handleRequestPayout = async () => {
    if (!affiliateToken) return;

    setRequestingPayout(true);
    setPayoutMessage(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/affiliate-portal/${affiliateToken}/payouts/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPayoutMessage({ type: 'success', text: data.message || 'Payout request submitted successfully!' });
        // Refresh payouts
        const payoutsResponse = await fetch(`${apiBaseUrl}/api/affiliate-portal/${affiliateToken}/payouts`);
        if (payoutsResponse.ok) {
          setPayouts(await payoutsResponse.json());
        }
      } else {
        setPayoutMessage({ type: 'error', text: data.error || 'Failed to request payout' });
      }
    } catch (err) {
      setPayoutMessage({ type: 'error', text: 'Network error requesting payout' });
    } finally {
      setRequestingPayout(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
    alert(`${label} copied to clipboard!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FC6A44] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading affiliate dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
          <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-lg font-semibold text-red-800">Access Error</h2>
          <p className="text-red-700 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!dashboard) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FC6A44] to-[#e85a36] text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Affiliate Dashboard</h1>
              <p className="text-orange-100 mt-1">Welcome back, {dashboard.affiliate.name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-orange-200">Partner of</p>
              <p className="text-lg font-semibold">{dashboard.storeName}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500 uppercase tracking-wide">Total Sales</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">${dashboard.stats.totalSales.toFixed(2)}</p>
            <p className="text-sm text-gray-500 mt-1">{dashboard.stats.totalOrders} orders</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500 uppercase tracking-wide">Total Commissions</p>
            <p className="text-2xl font-bold text-green-600 mt-1">${dashboard.stats.totalCommissions.toFixed(2)}</p>
            <p className="text-sm text-gray-500 mt-1">{dashboard.affiliate.commissionRate}% rate</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500 uppercase tracking-wide">Unpaid Balance</p>
            <p className="text-2xl font-bold text-amber-600 mt-1">${dashboard.stats.unpaidCommissions.toFixed(2)}</p>
            <p className="text-sm text-gray-500 mt-1">Available for payout</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500 uppercase tracking-wide">Paid Out</p>
            <p className="text-2xl font-bold text-[#FC6A44] mt-1">${dashboard.stats.paidCommissions.toFixed(2)}</p>
            <p className="text-sm text-gray-500 mt-1">Lifetime earnings</p>
          </div>
        </div>

        {/* Referral Links Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Referral Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Referral Link</label>
              <div className="flex">
                <input
                  type="text"
                  readOnly
                  value={dashboard.referralLink}
                  className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 bg-gray-50 text-sm"
                />
                <button
                  onClick={() => copyToClipboard(dashboard.referralLink, 'Referral link')}
                  className="px-4 py-2 bg-[#FC6A44] text-white rounded-r-lg hover:bg-[#e85a36] transition-colors"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Share this link with potential customers</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Code</label>
              <div className="flex">
                <input
                  type="text"
                  readOnly
                  value={dashboard.discountCode}
                  className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 bg-gray-50 text-sm font-mono"
                />
                <button
                  onClick={() => copyToClipboard(dashboard.discountCode, 'Discount code')}
                  className="px-4 py-2 bg-[#FC6A44] text-white rounded-r-lg hover:bg-[#e85a36] transition-colors"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Customers get {dashboard.affiliate.customerDiscountRate}% off with this code
              </p>
            </div>
          </div>

          {/* Social Share Buttons */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">Share on Social Media</label>
            <div className="flex flex-wrap gap-3">
              {/* Facebook */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(dashboard.referralLink)}&quote=${encodeURIComponent(`Get ${dashboard.affiliate.customerDiscountRate}% off at ${dashboard.storeName} with my code ${dashboard.discountCode}!`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="text-sm font-medium">Facebook</span>
              </a>

              {/* Instagram (Copy for Stories) */}
              <button
                onClick={() => {
                  const text = `🎉 Get ${dashboard.affiliate.customerDiscountRate}% off at ${dashboard.storeName}!\n\nUse my code: ${dashboard.discountCode}\n\n${dashboard.referralLink}`;
                  copyToClipboard(text, 'Instagram caption');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span className="text-sm font-medium">Instagram</span>
              </button>

              {/* TikTok (Copy for Bio/Posts) */}
              <button
                onClick={() => {
                  const text = `${dashboard.affiliate.customerDiscountRate}% OFF at ${dashboard.storeName}! 🛍️ Code: ${dashboard.discountCode} ${dashboard.referralLink}`;
                  copyToClipboard(text, 'TikTok caption');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
                <span className="text-sm font-medium">TikTok</span>
              </button>

              {/* X (Twitter) */}
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Get ${dashboard.affiliate.customerDiscountRate}% off at ${dashboard.storeName}! Use my code: ${dashboard.discountCode}`)}&url=${encodeURIComponent(dashboard.referralLink)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span className="text-sm font-medium">X</span>
              </a>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Click to share directly or copy text for Instagram/TikTok posts
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'overview'
                    ? 'border-[#FC6A44] text-[#FC6A44]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('sales')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'sales'
                    ? 'border-[#FC6A44] text-[#FC6A44]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Sales History
              </button>
              <button
                onClick={() => setActiveTab('payouts')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'payouts'
                    ? 'border-[#FC6A44] text-[#FC6A44]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Payouts
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Account Details</h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Affiliate Code</dt>
                        <dd className="font-mono">{dashboard.affiliate.affiliateCode}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Commission Rate</dt>
                        <dd>{dashboard.affiliate.commissionRate}%</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Customer Discount</dt>
                        <dd>{dashboard.affiliate.customerDiscountRate}%</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Status</dt>
                        <dd>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            dashboard.affiliate.status === 'Active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {dashboard.affiliate.status}
                          </span>
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Member Since</dt>
                        <dd>{new Date(dashboard.affiliate.createdAt).toLocaleDateString()}</dd>
                      </div>
                    </dl>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Performance Summary</h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Total Orders</dt>
                        <dd className="font-semibold">{dashboard.stats.totalOrders}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Total Revenue Generated</dt>
                        <dd className="font-semibold">${dashboard.stats.totalSales.toFixed(2)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Lifetime Commissions</dt>
                        <dd className="font-semibold text-green-600">${dashboard.stats.totalCommissions.toFixed(2)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Avg Order Value</dt>
                        <dd className="font-semibold">
                          ${dashboard.stats.totalOrders > 0
                            ? (dashboard.stats.totalSales / dashboard.stats.totalOrders).toFixed(2)
                            : '0.00'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            )}

            {/* Sales Tab */}
            {activeTab === 'sales' && (
              <div>
                {sales.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-gray-500">No sales recorded yet</p>
                    <p className="text-sm text-gray-400 mt-1">Share your referral link to start earning!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Order Total</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sales.map((sale) => (
                          <tr key={sale.id}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                              #{sale.orderId}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {new Date(sale.orderDate).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                              ${sale.orderTotal.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-600 text-right">
                              ${sale.commissionAmount.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                sale.paymentStatus === 'Paid'
                                  ? 'bg-green-100 text-green-800'
                                  : sale.paymentStatus === 'Pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {sale.paymentStatus}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Payouts Tab */}
            {activeTab === 'payouts' && (
              <div className="space-y-6">
                {/* Payout Request Section */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-6 border border-[#FC6A44]/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">Available for Payout</h3>
                      <p className="text-3xl font-bold text-[#FC6A44] mt-1">
                        ${payouts?.pendingPayoutAmount.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">Minimum payout: $50.00</p>
                    </div>
                    <button
                      onClick={handleRequestPayout}
                      disabled={requestingPayout || (payouts?.pendingPayoutAmount || 0) < 50}
                      className="px-6 py-3 bg-[#FC6A44] text-white rounded-lg hover:bg-[#e85a36] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {requestingPayout ? 'Requesting...' : 'Request Payout'}
                    </button>
                  </div>
                  {payoutMessage && (
                    <div className={`mt-4 p-3 rounded-lg ${
                      payoutMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {payoutMessage.text}
                    </div>
                  )}
                </div>

                {/* Payout History */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Payout History</h3>
                  {!payouts?.payouts.length ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No payouts yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Requested</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processed</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {payouts.payouts.map((payout) => (
                            <tr key={payout.id}>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {new Date(payout.requestedAt).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                                ${payout.amount.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {payout.method}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  payout.status === 'Paid'
                                    ? 'bg-green-100 text-green-800'
                                    : payout.status === 'Pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : payout.status === 'Processing'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {payout.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {payout.processedAt ? new Date(payout.processedAt).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
